import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
	Modal,
	ModalHeader,
	ModalBody
} from 'reactstrap';

import { DangerAlert } from '@techchange/components';
import { messages } from '../../i18n';

import ResourceSettingsEditor from '../ResourceSettingsEditor';

import './ResourceSettingsModal.scss';

const propTypes = {

	/**
	 * The resource to display.
	 */
	resource: PropTypes.shape({

		/**
		 * The resource ID.
		 */
		id: PropTypes.string,

		/**
		 * The resource title.
		 */
		title: PropTypes.string,

		/**
		 * The open date for the resource.
		 */
		open_date: PropTypes.string,

		/**
		 * The ID for the default child resource to display.
		 */
		default_child_id: PropTypes.string,

		/**
		 * Flag indicating if the title should be shown.
		 */
		show_title: PropTypes.bool,

		/**
		 * The metadata for the resource.
		 */
		data: PropTypes.shape({

			/**
			 * The associated form ID (if any) for the resource.
			 */
			form_id: PropTypes.string,

		}),

	}),

	/**
	 * The associated form (if any) for the resource.
	 */
	form: PropTypes.shape({

		/**
		 * The form ID.
		 */
		id: PropTypes.string,

		/**
		 * True, if learners are allowed to edit form entry submissions.
		 */
		allow_editing_submissions: PropTypes.bool,

		/**
		 * True, if learners are allowed to submit multiple form entries.
		 */
		allow_multiple_submissions: PropTypes.bool,

		/**
		 * The percentage grade needed to pass the quiz (if any).
		 */
		quiz_result_pass_percentage: PropTypes.number,

		/**
		 * True, if learners are allowed to see the correct answers after their submission.
		 */
		quiz_result_show_correct_answers: PropTypes.bool,

	}),

	/**
	 * The current user's timezone.
	 */
	userTimezone: PropTypes.string.isRequired,

	/**
	 * True, if the resource settings modal is visible.
	 */
	isOpen: PropTypes.bool.isRequired,

	/**
	 * True, if there was an error saving the resource settings modal.
	 */
	submitError: PropTypes.bool.isRequired,

	/**
	 * Function to handle deleting the resource.
	 */
	onDelete: PropTypes.func.isRequired,

	/**
	 * Function to handle saving the resource settings modal.
	 */
	onSave: PropTypes.func.isRequired,

	/**
	 * Function to handle toggling the resource settings modal.
	 */
	onToggle: PropTypes.func.isRequired,

};

const defaultProps = {
	submitError: false,
};

const ResourceSettingsModal = (props) => {

	const {
		resource,
		form,
		userTimezone,
		isOpen,
		submitError,
		onDelete,
		onSave,
		onToggle,
	} = props;

	return (
		<Modal
			size="lg"
			isOpen={isOpen}
			toggle={onToggle}>
			<ModalHeader
				tag="h2"
				toggle={onToggle}>
				<FormattedMessage
					{...messages.TC_COMPONENTS_RESOURCE_SETTINGS_MODAL_HEADER}
					values={{ title: resource.title }} />
			</ModalHeader>
			<ModalBody>
				{submitError &&
					<DangerAlert>
						<FormattedMessage
							{...messages.TC_COMPONENTS_RESOURCE_SETTINGS_MODAL_ERROR_MSG_SAVE_RESOURCE_SETTINGS} />
					</DangerAlert>
				}
				<ResourceSettingsEditor
					resource={resource}
					form={form}
					userTimezone={userTimezone}
					onCancel={onToggle}
					onDelete={onDelete}
					onSave={onSave} />
			</ModalBody>
		</Modal>
	);

};

ResourceSettingsModal.defaultProps = defaultProps;
ResourceSettingsModal.propTypes = propTypes;

export default ResourceSettingsModal;
